import {getAbFlags} from '@amplify/abflag';
import {AppAbFlag} from '@amplify/athena-models';
import {enableStaticRendering} from 'mobx-react';
import {AppProps} from 'next/app';
import React, {PropsWithChildren, useContext, useEffect, useState, createContext, useRef, ComponentType} from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import {RootStore} from '../store/Store';
import {LoadingState} from './LoadingState/LoadingState';
import Feature from './FeatureFlag/Flags';
import {FORM_LPV1_PARAMETER} from './CustomerInformationV2/constants';

const isServer = typeof window === 'undefined';
enableStaticRendering(isServer);

const BACKUP_AB_FLAG_RESULT = [
    {
        flagName: 'emptyAbflagResult',
        probabilityTrue: 1,
    },
];

export const StoreContext = createContext<RootStore>(null);

export function useStore() {
    return useContext(StoreContext);
}

export type StoreProviderProps = {
    abFlagScheme: AppAbFlag[];
};

export function StoreProvider({abFlagScheme, children}: PropsWithChildren<StoreProviderProps>) {
    const [store, setStore] = useState(null);
    const isInitialized = useRef(false);

    useEffect(() => {
        if (!isInitialized.current && abFlagScheme) {
            const init = async () => {
                const params = new URLSearchParams(window.location.search);
                console.log('abFlagScheme ' + JSON.stringify(abFlagScheme));
                const abFlags = getAbFlags(abFlagScheme || []);

                // AB experience for Meta traffic
                const source = params.get('utm_source')?.toLowerCase();
                const isMetaTraffic = source === 'fb' || source === 'ig';
                let mqlExperienceMetaTraffic4198 = false;

                if (isMetaTraffic) {
                    mqlExperienceMetaTraffic4198 = !!abFlags[Feature.mqlExperienceMetaTraffic4198];
                } else {
                    mqlExperienceMetaTraffic4198 = false;
                }

                const experiment = {
                    ...abFlags,
                    [Feature.mqlExperienceMetaTraffic4198]: mqlExperienceMetaTraffic4198,
                };

                const store = await RootStore.init({
                    experiment,
                    hubId: params.get('hubId'),
                    userId: params.get('userId'),
                });

                // If there is a value in experiment, use it
                let finalNewLandingPage4050: boolean;
                if (typeof store.userData.experiment?.[Feature.newLandingPage4050] === 'boolean') {
                    finalNewLandingPage4050 = store.userData.experiment[Feature.newLandingPage4050];
                } else {
                    // Define for a new user only once
                    if (params.get('landingpage') === FORM_LPV1_PARAMETER) {
                        finalNewLandingPage4050 = true;
                    } else if (mqlExperienceMetaTraffic4198 === true && isMetaTraffic) {
                        finalNewLandingPage4050 = true;
                    } else {
                        finalNewLandingPage4050 = false;
                    }
                    store.userData.experiment[Feature.newLandingPage4050] = finalNewLandingPage4050;
                }

                // Keep landingPage field in sync for UI logic
                if (finalNewLandingPage4050) {
                    store.userData.landingPage = FORM_LPV1_PARAMETER;
                } else {
                    store.userData.landingPage = null;
                }

                isInitialized.current = true;
                if (process.env.NODE_ENV === 'development') {
                    (window as any).__store__ = store;
                }
                setStore(store);
            };
            init();
        }
    }, [abFlagScheme]);

    if (!store) return <LoadingState height="100vh" />;

    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

export type PageProps = {
    abFlagScheme: AppAbFlag[];
};

export function withStore<T extends AppProps<PageProps>>(Component: ComponentType<T>) {
    function ComponentWithStore(props: T) {
        let abFlagScheme = props.pageProps.abFlagScheme;

        if (!abFlagScheme || !abFlagScheme.length) {
            console.error(
                'abFlagScheme not found! Please export getStaticProps on each page. Falling bak to backup abFlagScheme',
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            abFlagScheme = BACKUP_AB_FLAG_RESULT as any; // FIXME: any here until models updated
        }

        return (
            <StoreProvider abFlagScheme={abFlagScheme}>
                <Component {...props} />
            </StoreProvider>
        );
    }

    hoistNonReactStatics(ComponentWithStore, Component);

    return ComponentWithStore;
}
